import { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Send, History, Search, Star, Copy, User, Building2, Handshake, Video, ArrowLeft, Loader2, Sparkles, FileBarChart } from "lucide-react";
import { useFavorites } from "@/contexts/FavoritesContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { people, companies, deals, meetings as dummyMeetings, notes as dummyNotes } from "@/data/dummy";
const MeetingBriefingSheet = lazy(() => import("@/components/shared/MeetingBriefingSheet"));
const MeetingIntelligenceSheet = lazy(() => import("@/components/shared/MeetingIntelligenceSheet"));
import { useChatAgent } from "@/lib/hooks/use-chat-agent";
import { useAiChatHistory } from "@/lib/hooks/use-ai-chat-history";
import type { AiChatConversationItem } from "@/lib/hooks/use-ai-chat-history";
import type { ChatApproval, ChatContext, ChatMessage, HistoryMessage } from "@/lib/types/chat";
import { useNotesSubjectSearchLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useEntitySheetParams } from "@/lib/hooks/use-entity-sheet-params";
import { isLovablePreview } from "@/lib/utils/environment";
import { getDummyResponse } from "@/data/dummyAskCoffeeResponses";
import { classifyAskCoffeeIntent } from "@/lib/askCoffeeIntent";
import { useIsReadOnly } from "@/lib/rbac";
import { useFeatureFlag } from "@/lib/feature-flags";
import { FEATURE_FLAGS } from "@/lib/feature-flags/registry";
import { REPORT_TEMPLATE_META, type ReportTemplate } from "@/data/reports/types";
import { isTemplateAvailable } from "@/data/reports/availability";
import AskCoffeeFeedback from "@/components/shared/AskCoffeeFeedback";
import AskCoffeeApprovalCard from "@/components/shared/AskCoffeeApprovalCard";
import { copyRichText } from "@/lib/utils/copyRichText";
import { useThinkingSubtext } from "@/lib/hooks/use-thinking-subtext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";

// Keys, not literals: this array is module scope, so a `t()` here would capture
// the language at import and never update. Resolved per render below.
const SUGGESTION_KEYS = [
  "askCoffee.suggestionWhatCanYouDo",
  "askCoffee.suggestionDealsNeedAttention",
  "askCoffee.suggestionFollowUp",
] as const;

/**
 * Sentinel the assistant appends to a reply so the panel renders the
 * report-template chips. A wire token, never shown to the user.
 */
const BUILD_REPORT_CHIPS = "[BUILD_REPORT_CHIPS]";

/**
 * Wire values of `searchType` mapped to their catalog keys, so the history
 * line reads in the user's language instead of echoing the enum.
 */
const SEARCH_TYPE_LABEL_KEYS: Record<string, string> = {
  person: "askCoffee.historyTypePerson",
  company: "askCoffee.historyTypeCompany",
  deal: "askCoffee.historyTypeDeal",
  meeting: "askCoffee.historyTypeMeeting",
};

const DUMMY_HISTORY: (AiChatConversationItem & { historyType?: "prompt" | "search"; searchType?: "person" | "company" | "deal"; searchEntity?: string })[] = [
  { id: 1, agentSessionId: "s1", contextType: "general", firstMessage: "Which deals are at risk this quarter?", relativeTime: "2 hours ago", updateTs: "2026-03-30T10:00:00Z", historyType: "prompt" },
  { id: 2, agentSessionId: "s2", contextType: "general", firstMessage: "Sarah Chen", relativeTime: "3 hours ago", updateTs: "2026-03-30T09:00:00Z", historyType: "search", searchType: "person", searchEntity: "Sarah Chen" },
  { id: 3, agentSessionId: "s3", contextType: "general", firstMessage: "Who should I follow up with this week?", relativeTime: "5 hours ago", updateTs: "2026-03-30T07:00:00Z", historyType: "prompt" },
  { id: 4, agentSessionId: "s4", contextType: "general", firstMessage: "Stripe", relativeTime: "1 day ago", updateTs: "2026-03-29T14:00:00Z", historyType: "search", searchType: "company", searchEntity: "Stripe" },
  { id: 5, agentSessionId: "s5", contextType: "general", firstMessage: "What's the status of the Shopify deal?", relativeTime: "1 day ago", updateTs: "2026-03-29T10:00:00Z", historyType: "prompt" },
  { id: 6, agentSessionId: "s6", contextType: "general", firstMessage: "Notion Enterprise Plan", relativeTime: "2 days ago", updateTs: "2026-03-28T16:00:00Z", historyType: "search", searchType: "deal", searchEntity: "Notion Enterprise Plan" },
  { id: 7, agentSessionId: "s7", contextType: "general", firstMessage: "How is Sarah Chen's engagement trending?", relativeTime: "2 days ago", updateTs: "2026-03-28T11:00:00Z", historyType: "prompt" },
  { id: 8, agentSessionId: "s8", contextType: "general", firstMessage: "Datadog", relativeTime: "3 days ago", updateTs: "2026-03-27T09:00:00Z", historyType: "search", searchType: "company", searchEntity: "Datadog" },
  { id: 9, agentSessionId: "s9", contextType: "general", firstMessage: "Compare Q1 vs Q2 pipeline coverage", relativeTime: "3 days ago", updateTs: "2026-03-27T08:00:00Z", historyType: "prompt" },
  { id: 10, agentSessionId: "s10", contextType: "general", firstMessage: "Marcus Johnson", relativeTime: "4 days ago", updateTs: "2026-03-26T15:00:00Z", historyType: "search", searchType: "person", searchEntity: "Marcus Johnson" },
  { id: 11, agentSessionId: "s11", contextType: "general", firstMessage: "Which companies have gone quiet recently?", relativeTime: "4 days ago", updateTs: "2026-03-26T10:00:00Z", historyType: "prompt" },
  { id: 12, agentSessionId: "s12", contextType: "general", firstMessage: "Stripe Developer Tools", relativeTime: "5 days ago", updateTs: "2026-03-25T14:00:00Z", historyType: "search", searchType: "deal", searchEntity: "Stripe Developer Tools" },
  { id: 13, agentSessionId: "s13", contextType: "general", firstMessage: "Prep me for my meeting with Blackstone tomorrow", relativeTime: "5 days ago", updateTs: "2026-03-25T09:00:00Z", historyType: "prompt" },
  { id: 14, agentSessionId: "s14", contextType: "general", firstMessage: "Priya Patel", relativeTime: "1 week ago", updateTs: "2026-03-23T11:00:00Z", historyType: "search", searchType: "person", searchEntity: "Priya Patel" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
  initialResponse?: string;
  initialContext?: ChatContext;
  contextTitle?: string;
  contextSubtitle?: string;
  onBack?: () => void;
}

// Build entity lookup maps for linking bold names in AI responses
const useEntityLookups = () => {
  return useMemo(() => {
    const personMap = new Map(people.map(p => [p.name.toLowerCase(), p]));
    const companyMap = new Map(companies.map(c => [c.name.toLowerCase(), c]));
    const dealMap = new Map(deals.map(d => [d.name.toLowerCase(), d]));
    return { personMap, companyMap, dealMap };
  }, []);
};

export default function AskCoffeePanel({ open, onOpenChange, initialQuery, initialResponse, initialContext, contextTitle, contextSubtitle, onBack }: Props) {
  const { t } = useTranslation();
  const isReadOnly = useIsReadOnly();
  const [input, setInput] = useState("");
  const [dummyMessages, setDummyMessages] = useState<{ role: "user" | "ai"; text: string; id: string; backendMessageId?: number; vote?: number }[]>([]);
  const [dummyThinking, setDummyThinking] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingResume, setPendingResume] = useState<AiChatConversationItem | null>(null);
  const [isResumingLoading, setIsResumingLoading] = useState(false);
  const prevMessagesLoadingRef = useRef(false);
  const isResumingRef = useRef(false);
  const prevSessionIdRef = useRef("");
  const resumeTokenRef = useRef(0);
  const activeResumeTokenRef = useRef<number | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
  }, []);
  const { isFavorite, toggleFavorite } = useFavorites();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [briefingMeeting, setBriefingMeeting] = useState<any>(null);
  const [intelligenceNote, setIntelligenceNote] = useState<any>(null);

  const { personMap, companyMap, dealMap } = useEntityLookups();
  const navigate = useNavigate();
  const { openEntitySheet } = useEntitySheetParams();

  // Open person/company/deal sheets via app-level URL params so the popup is shareable
  // (AppLayout renders the actual sheet; AskCoffeePanel stays open underneath).
  // Signatures stay entity-shaped: these are passed as props into result
  // components, so only the bodies move to the shared hook.
  const setDetailPerson = useCallback((entity: any) => {
    if (entity?.id) openEntitySheet('personSheet', entity.id);
  }, [openEntitySheet]);
  const setDetailCompany = useCallback((entity: any) => {
    if (entity?.id) openEntitySheet('companySheet', entity.id);
  }, [openEntitySheet]);
  const setDetailDeal = useCallback((entity: any) => {
    if (entity?.id) openEntitySheet('dealSheet', entity.id);
  }, [openEntitySheet]);
  const reportsEnabled = useFeatureFlag(FEATURE_FLAGS.REPORTS);

  const handleBuildReport = useCallback((variant: "full" | "short" = "full") => {
    // The canned exchange below writes to `dummyMessages`, which only renders in
    // preview — in production it would be a silent no-op, so send the user to
    // the Reports page instead. Wiring this to the real agent is a later phase.
    if (!isLovablePreview()) {
      onOpenChange(false);
      navigate("/reports");
      return;
    }
    const userMsgId = `user-build-${Date.now()}`;
    const aiMsgId = `ai-build-${Date.now()}`;
    setDummyMessages(prev => [...prev, { id: userMsgId, role: "user", text: "Help me build a report" }]);
    setDummyThinking(true);
    setTimeout(() => {
      // The example prompts below mirror `SUGGESTIONS` in ReportsPage: they
      // stick to reports the CRM can answer — Coffee has no lead source field.
      const fullLines = [
        "Happy to help. I can build any report you can describe in plain English — just tell me what you want to see, and I'll generate it with the right charts, KPIs, and filters. Once it looks right, you can save it to **Favorites → Reports** and re-open it anytime.",
        "",
        "Here are **5 ready-made reports** to get you started — click one to run it, or describe your own. For example:",
        "",
        "- *“Show me deals over $50K closing in the next 30 days, grouped by owner”*",
        "- *“Show me which reps haven't logged activity in the past week”*",
        "",
        BUILD_REPORT_CHIPS,
      ];
      const shortLines = [
        "Happy to help — describe the report you'd like and I'll generate it with the right charts, KPIs, and filters. Once it looks right, you can save it to **Favorites → Reports** and re-open it anytime.",
        "",
        "Some examples to get you started:",
        "",
        "- *“Show me deals over $50K closing in the next 30 days, grouped by owner”*",
        "- *“Show me which reps haven't logged activity in the past week”*",
      ];
      const lines = variant === "short" ? shortLines : fullLines;
      setDummyMessages(prev => [...prev, { id: aiMsgId, role: "ai", text: lines.join("\n") }]);
      setDummyThinking(false);
    }, 800);
  }, [navigate, onOpenChange]);

  // Custom markdown components. Entity links (/people|/companies|/deals/<id>) open
  // the detail sheet inline (no URL change), matching the search-result behavior.
  // Other internal links use SPA nav. External links open in a new tab.
  const markdownComponents = useMemo(() => ({
    a: ({ href, children, ...props }: any) => {
      const isInternal = typeof href === "string" && href.startsWith("/");
      if (isInternal) {
        return (
          <a
            href={href}
            className="text-primary hover:underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              const entityMatch = href.match(/^\/(people|companies|deals)\/(.+)$/);
              if (entityMatch) {
                const [, kind, rawId] = entityMatch;
                const id = decodeURIComponent(rawId);
                if (kind === "people") setDetailPerson({ id });
                else if (kind === "companies") setDetailCompany({ id });
                else setDetailDeal({ id });
                return;
              }
              navigate(href);
            }}
            {...props}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" {...props}>
          {children}
        </a>
      );
    },
    strong: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children
        : Array.isArray(children) ? children.map((c: any) => typeof c === 'string' ? c : '').join('')
        : '';
      if (!text) return <strong {...props}>{children}</strong>;

      // Strip trailing metadata like " — $85,000 · Negotiation stage"
      const cleanName = text.split(/\s*[—–·]\s*/)[0].trim().toLowerCase();

      const deal = dealMap.get(cleanName);
      if (deal) {
        return (
          <button
            className="font-semibold text-primary hover:underline cursor-pointer inline"
            onClick={() => setDetailDeal(deal)}
          >
            {children}
          </button>
        );
      }

      const person = personMap.get(cleanName);
      if (person) {
        return (
          <button
            className="font-semibold text-primary hover:underline cursor-pointer inline"
            onClick={() => setDetailPerson(person)}
          >
            {children}
          </button>
        );
      }

      const company = companyMap.get(cleanName);
      if (company) {
        return (
          <button
            className="font-semibold text-primary hover:underline cursor-pointer inline"
            onClick={() => setDetailCompany(company)}
          >
            {children}
          </button>
        );
      }

      return <strong {...props}>{children}</strong>;
    },
  }), [personMap, companyMap, dealMap, navigate, setDetailPerson, setDetailCompany, setDetailDeal]);

  const workspaceId = useWorkspaceId();
  const [searchQuery, { data: searchData }] = useNotesSubjectSearchLazyQuery({ fetchPolicy: 'no-cache' });
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const { search: historySearch, setSearch: setHistorySearch, conversations: historyConversations, loading: historyLoading, openHistory, loadMessages, messages: historicMessages, messagesLoading, messagesError, recordEntitySearch } = useAiChatHistory({ enabled: open });

  const allConversations = historyConversations;
  const chatConfig = useMemo(() => ({
    initialContext: initialContext || { type: "ask-ai" as const },
    initializeSession: open,
    // Writes wait for an approval card here; the agent's own flag still decides
    // whether it pauses at all.
    approvalCards: true,
  }), [initialContext, open]);

  const { state, actions } = useChatAgent(chatConfig);
  const { messages: chatMessages, currentResponse, processingState, toolStatus, sessionId } = state;

  // Debounced backend entity search
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const q = input.trim();
    if (q.length < 2 || isLovablePreview() || !workspaceId) return;
    debounceTimerRef.current = setTimeout(() => {
      searchQuery({ variables: { workspaceId, search: q, first: 6 } });
    }, 300);
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, [input, workspaceId, searchQuery]);

  const autocompleteResults = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (q.length < 2) return [];

    if (isLovablePreview()) {
      // Fallback to dummy data in preview mode
      const results: { type: "person" | "company" | "deal" | "meeting"; id: string; name: string; subtitle: string; entity: any }[] = [];
      people.filter(p => p.name.toLowerCase().includes(q)).slice(0, 3).forEach(p =>
        results.push({ type: "person", id: p.id, name: p.name, subtitle: `${p.title} at ${p.company}`, entity: p })
      );
      companies.filter(c => c.name.toLowerCase().includes(q)).slice(0, 3).forEach(c =>
        results.push({ type: "company", id: c.id, name: c.name, subtitle: c.industry, entity: c })
      );
      deals.filter(d => d.name.toLowerCase().includes(q) || d.company.toLowerCase().includes(q)).slice(0, 3).forEach(d =>
        results.push({ type: "deal", id: d.id, name: d.name, subtitle: `${d.company} · ${d.stage}`, entity: d })
      );
      dummyMeetings.filter(m => m.title.toLowerCase().includes(q)).slice(0, 3).forEach(m =>
        results.push({ type: "meeting", id: m.id, name: m.title, subtitle: `${m.date} · ${m.time}`, entity: m })
      );
      return results.slice(0, 8);
    }

    // Use backend search results
    const results: { type: "person" | "company" | "deal"; id: string; name: string; subtitle: string; entity: any }[] = [];
    const contacts = searchData?.contacts?.edges?.map(e => e?.node).filter(Boolean) || [];
    const accounts = searchData?.accounts?.edges?.map(e => e?.node).filter(Boolean) || [];
    const dealResults = searchData?.deals?.edges?.map(e => e?.node).filter(Boolean) || [];

    contacts.slice(0, 3).forEach(c => {
      const name = [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail || 'Unknown';
      const companyName = c.accounts?.edges?.[0]?.node?.properties?.name || '';
      results.push({
        type: "person", id: c.id, name,
        subtitle: [c.properties?.jobTitle, companyName].filter(Boolean).join(' at '),
        entity: {
          id: c.id, name,
          title: c.properties?.jobTitle, company: companyName,
          email: c.primaryEmail, phone: c.properties?.phone,
          location: [c.properties?.city, c.properties?.state].filter(Boolean).join(', '),
          linkedin: c.properties?.linkedinProfileUrl,
        },
      });
    });
    accounts.slice(0, 3).forEach(a => {
      const name = a.properties?.name || a.domain || 'Unknown';
      results.push({
        type: "company", id: a.id, name,
        subtitle: a.properties?.industry || a.domain || '',
        entity: {
          id: a.id, name, domain: a.domain,
          website: a.domain ? `https://${a.domain}` : undefined,
          industry: a.properties?.industry, location: a.location,
          employees: a.properties?.numberOfEmployees?.toString(),
          description: a.properties?.description,
        },
      });
    });
    dealResults.slice(0, 3).forEach(d => {
      const name = d.properties?.name || 'Unknown';
      const companyName = d.accounts?.edges?.[0]?.node?.properties?.name || '';
      results.push({
        type: "deal", id: d.id, name,
        subtitle: [companyName, d.properties?.stage].filter(Boolean).join(' \u00b7 '),
        entity: {
          id: d.id, name, company: companyName,
          stage: d.properties?.stage, amount: d.properties?.amount,
          closeDate: d.properties?.closeDate,
        },
      });
    });
    return results.slice(0, 6);
  }, [input, searchData]);

  const handleAutocompleteSelect = useCallback((result: { type: string; id: string; name?: string; entity?: any }) => {
    setInput("");
    if (result.name && (result.type === "person" || result.type === "company" || result.type === "deal" || result.type === "meeting")) {
      recordEntitySearch(result.type, result.name);
    }
    if (result.type === "person") {
      setDetailPerson(result.entity);
    } else if (result.type === "company") {
      setDetailCompany(result.entity);
    } else if (result.type === "deal") {
      setDetailDeal(result.entity);
    } else if (result.type === "meeting") {
      const meeting = result.entity;
      const meetingDate = new Date(meeting.date);
      const isPast = meetingDate < new Date();
      if (isPast) {
        // Find matching note for MeetingIntelligenceSheet
        const matchingNote = dummyNotes.find(n => n.name.toLowerCase().includes(meeting.title.toLowerCase().split(' ')[0]));
        setIntelligenceNote(matchingNote || { id: meeting.id, name: meeting.title, company: meeting.attendees?.[0] || "", people: meeting.attendees || [], date: meeting.date });
      } else {
        setBriefingMeeting(meeting);
      }
    }
  }, [recordEntitySearch]);

  const displayMessages = useMemo(() => {
    if (isLovablePreview()) return dummyMessages;
    return chatMessages.map(msg => ({
      role: msg.isUser ? "user" as const : "ai" as const,
      text: msg.content,
      id: msg.id,
      backendMessageId: msg.backendMessageId,
      vote: msg.vote,
      approval: msg.approval,
    }));
  }, [chatMessages, dummyMessages]);

  const isThinking = isLovablePreview() ? dummyThinking : (processingState === "processing" || processingState === "streaming");

  // Shared with the agent builder — see use-thinking-subtext.ts for the sticky-label
  // rule and why labels are rendered verbatim.
  const thinkingSubtext = useThinkingSubtext(toolStatus.activeTools, isThinking, {
    idle: "Understanding your question",
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayMessages, currentResponse, isThinking]);

  const sentInitialQueryRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (initialQuery && !sentInitialQueryRef.current) {
        sentInitialQueryRef.current = true;
        if (isLovablePreview()) {
          // Use dummy response in preview mode
          const userMsg = { id: `user-init-${Date.now()}`, role: "user" as const, text: initialQuery };
          setDummyMessages(prev => [...prev, userMsg]);
          setDummyThinking(true);
          setTimeout(() => {
            const response = getDummyResponse(initialQuery);
            const aiMsg = { id: `ai-init-${Date.now()}`, role: "ai" as const, text: response };
            setDummyMessages(prev => [...prev, aiMsg]);
            setDummyThinking(false);
          }, 800 + Math.random() * 700);
        } else {
          actions.sendMessage(initialQuery);
        }
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      sentInitialQueryRef.current = false;
    }
  }, [open, initialQuery, actions]);

  // Auto-resume conversation when messages finish loading
  useEffect(() => {
    const wasLoading = prevMessagesLoadingRef.current;
    prevMessagesLoadingRef.current = messagesLoading;

    if (wasLoading && !messagesLoading && pendingResume) {
      if (historicMessages.length > 0) {
        const uiMessages: ChatMessage[] = historicMessages.map((m, idx) => ({
          id: `history-${m.id}-${idx}`,
          content: m.content,
          isUser: m.role === 'user',
          timestamp: new Date(m.createTs),
          backendMessageId: m.id,
          vote: m.vote ?? undefined,
        }));
        const historyPayload: HistoryMessage[] = historicMessages.slice(-20).map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
        actions.resumeConversation(uiMessages, historyPayload, pendingResume.id);
        setPendingResume(null);
        // Keep isResumingLoading = true — cleared when agent session is created
      } else {
        // Empty history — query completed but returned nothing. Without this branch,
        // the spinner stayed on forever because resumeConversation never fires.
        if (resumeTimeoutRef.current) {
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = null;
        }
        activeResumeTokenRef.current = null;
        isResumingRef.current = false;
        setIsResumingLoading(false);
        setPendingResume(null);
        toast.error(t("askCoffee.historyLoadFailed"));
      }
    }
  }, [messagesLoading, pendingResume, historicMessages, actions]);

  // Clear loader once the agent session is created after a resume
  useEffect(() => {
    const prevId = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;

    if (isResumingRef.current && !prevId && sessionId) {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      activeResumeTokenRef.current = null;
      isResumingRef.current = false;
      setIsResumingLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [sessionId]);

  // Surface resume failures (GraphQL error or createSession error) so the
  // spinner doesn't sit indefinitely. Errors only matter while resuming.
  useEffect(() => {
    if (!isResumingLoading) return;
    if (!messagesError && !state.error) return;
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
    activeResumeTokenRef.current = null;
    isResumingRef.current = false;
    setIsResumingLoading(false);
    setPendingResume(null);
    toast.error(
      messagesError
        ? t("askCoffee.historyLoadFailed")
        : t("askCoffee.resumeFailed")
    );
  }, [messagesError, state.error, isResumingLoading]);

  const handleDealIntent = useCallback(async (text: string, userMsgId: string) => {
    // Read-only users can't open the AddDealDialog at all — skip intent detection
    // entirely so the LLM's read tools handle the message instead.
    if (isReadOnly) return false;

    // Heuristic: only try the intent classifier when the user clearly asks
    // to create / add / log a new deal/opportunity. Avoids spending an AI
    // call on every casual lookup question.
    const looksLikeCreateDeal =
      /\b(create|add|new|log|make|start|open|register)\b.*\b(deal|opportunity|opp|sale|pipeline\s+entry)\b/i
        .test(text) ||
      /\b(deal|opportunity|opp)\b.*\b(for|with)\b/i.test(text);

    if (!looksLikeCreateDeal) return false;

    const result = await classifyAskCoffeeIntent(text);
    if (!result || result.intent !== "create_deal") return false;

    const reply =
      result.reply?.trim() ||
      `Got it — opening a new deal${result.deal?.company ? ` for **${result.deal.company}**` : ""}. Review the details and click Save to create it.`;
    const aiMsg = { id: `ai-${userMsgId}`, role: "ai" as const, text: reply };
    setDummyMessages(prev => [...prev, aiMsg]);

    window.dispatchEvent(
      new CustomEvent("askCoffeeOpenAddDeal", { detail: result.deal ?? {} }),
    );
    onOpenChange(false);
    return true;
  }, [onOpenChange, isReadOnly]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    setInput("");

    if (isLovablePreview()) {
      const userMsgId = `${Date.now()}`;
      const userMsg = { id: `user-${userMsgId}`, role: "user" as const, text };
      setDummyMessages(prev => [...prev, userMsg]);
      setDummyThinking(true);

      (async () => {
        // Simulate "real" thinking time so users see the coffee-themed
        // animations (Roasting / Grinding / Brewing...) cycle a few times
        // before the answer appears, instead of an instant cache hit.
        const thinkDelay = 2500 + Math.floor(Math.random() * 2000); // 2.5–4.5s
        let handled = false;
        try {
          const [intentHandled] = await Promise.all([
            handleDealIntent(text, userMsgId).catch(err => {
              console.warn("[AskCoffee] handleDealIntent failed:", err);
              return false;
            }),
            new Promise(resolve => setTimeout(resolve, thinkDelay)),
          ]);
          handled = !!intentHandled;
        } finally {
          setDummyThinking(false);
        }
        if (handled) return;
        const response = getDummyResponse(text);
        const aiMsg = { id: `ai-${userMsgId}`, role: "ai" as const, text: response };
        setDummyMessages(prev => [...prev, aiMsg]);
      })();
    } else {
      actions.sendMessage(text);
    }
  };

  const handleFavoriteQuery = (userMsg: string, aiMsg: string, aiId: string) => {
    toggleFavorite({ id: aiId, type: "query", name: userMsg.length > 50 ? userMsg.slice(0, 50) + "…" : userMsg, subtitle: "Saved AI Prompt", query: userMsg, response: aiMsg });
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    openHistory();
  };

  const handleHistoryItemClick = useCallback((item: typeof DUMMY_HISTORY[number]) => {
    setHistoryOpen(false);
    if (isLovablePreview() && item.historyType === "search" && item.searchType && item.searchEntity) {
      // For entity searches, find the matching entity and open detail sheet
      const q = item.searchEntity.toLowerCase();
      if (item.searchType === "person") {
        const person = people.find(p => p.name.toLowerCase() === q);
        if (person) { setDetailPerson(person); return; }
      } else if (item.searchType === "company") {
        const company = companies.find(c => c.name.toLowerCase() === q);
        if (company) { setDetailCompany(company); return; }
      } else if (item.searchType === "deal") {
        const deal = deals.find(d => d.name.toLowerCase() === q);
        if (deal) { setDetailDeal(deal); return; }
      } else if ((item.searchType as string) === "meeting") {
        const meeting = dummyMeetings.find(m => m.title.toLowerCase() === q);
        if (meeting) {
          const meetingDate = new Date(meeting.date);
          if (meetingDate < new Date()) {
            const matchingNote = dummyNotes.find(n => n.name.toLowerCase().includes(meeting.title.toLowerCase().split(' ')[0]));
            setIntelligenceNote(matchingNote || { id: meeting.id, name: meeting.title, company: meeting.attendees?.[0] || "", people: meeting.attendees || [], date: meeting.date } as any);
          } else {
            setBriefingMeeting(meeting);
          }
          return;
        }
      }
      // Fallback: treat as a prompt
      sendMessage(item.firstMessage || "");
    } else if (isLovablePreview()) {
      // For prompts in preview mode, send as a message
      sendMessage(item.firstMessage || "");
    } else {
      // Production: resume the conversation
      handleViewConversation(item);
    }
  }, [sendMessage]);

  const handleViewConversation = (conversation: AiChatConversationItem) => {
    resumeTokenRef.current += 1;
    const thisToken = resumeTokenRef.current;
    activeResumeTokenRef.current = thisToken;

    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    isResumingRef.current = true;
    setIsResumingLoading(true);
    setPendingResume(conversation);

    // Hard frontend timeout for cases where neither success nor a surfaced error
    // arrives (pathological slow response, stalled retry loop, etc).
    resumeTimeoutRef.current = setTimeout(() => {
      if (activeResumeTokenRef.current !== thisToken) return;
      activeResumeTokenRef.current = null;
      resumeTimeoutRef.current = null;
      isResumingRef.current = false;
      setIsResumingLoading(false);
      setPendingResume(null);
      toast.error(t("askCoffee.resumeTimedOut"));
    }, 30000);

    loadMessages(conversation.id);
    setHistoryOpen(false);
  };

  const handleNewChat = () => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
    activeResumeTokenRef.current = null;
    isResumingRef.current = false;
    actions.reset();
    setInput("");
    setDummyMessages([]);
    setDummyThinking(false);
    setPendingResume(null);
    setIsResumingLoading(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => {
        if (!o) {
          if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
            resumeTimeoutRef.current = null;
          }
          activeResumeTokenRef.current = null;
          isResumingRef.current = false;
          setPendingResume(null);
          setIsResumingLoading(false);
          actions.reset();
        }
        onOpenChange(o);
      }}>
        <SheetContent side="right" className="sm:max-w-[50vw] w-full p-0 flex flex-col gap-0" hideOverlay={!!onBack}>
          {/* Header */}
          <SheetHeader className="px-4 py-3 border-b border-border shrink-0">

            {onBack && (
              <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-2 transition">
                <ArrowLeft className="h-4 w-4" /> {t("askCoffee.back")}
              </button>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/logos/coffee-icon.svg" alt="Coffee" className="h-5 w-5" />
                <SheetTitle className="text-base font-display">{t("nav.askCoffee")}</SheetTitle>
              </div>
              <div className="flex items-center gap-1 mr-6">
                <button
                  onClick={handleNewChat}
                  className="text-xs px-2 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition"
                >
                  {t("askCoffee.newChat")}
                </button>
              </div>
            </div>
          </SheetHeader>

          {isReadOnly && (
            <div className="px-4 pt-3 pb-0">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
                {t("askCoffee.readOnlyNotice")}
              </div>
            </div>
          )}

          {contextTitle && (
            <div className="px-4 pt-3 pb-0">
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                <p className="text-sm font-medium">{contextTitle}</p>
                {contextSubtitle && <p className="text-xs text-muted-foreground mt-0.5">{contextSubtitle}</p>}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin">
            {isResumingLoading ? (
              <div className="flex justify-center items-center h-full">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : displayMessages.length === 0 && !currentResponse ? (
              <div className="flex flex-col h-full">
                <div className="flex flex-col items-center text-center pt-4 pb-3">
                  <img src="/logos/coffee-icon.svg" alt="Coffee" className="h-8 w-8 mb-3 opacity-60" />
                  <p className="text-muted-foreground text-xs mb-3">{t("askCoffee.howCanIHelp")}</p>
                  <div className="grid grid-cols-1 gap-2 w-full max-w-xs">
                    {SUGGESTION_KEYS.map(key => {
                      const suggestion = t(key);
                      return (
                        <button key={key} onClick={() => sendMessage(suggestion)} className="text-left text-xs rounded-lg border border-border bg-card p-2.5 hover:bg-muted transition">
                          {suggestion}
                        </button>
                      );
                    })}
                    {reportsEnabled && (
                      <button
                        onClick={() => handleBuildReport()}
                        className="text-left text-xs rounded-lg border border-border bg-card p-2.5 hover:bg-muted transition"
                      >
                        {t("askCoffee.buildReport")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {displayMessages.map((m, i) => {
                  const precedingUserMsg = m.role === "ai" && i > 0 ? displayMessages[i - 1]?.text : "";
                  const queryFavorited = m.role === "ai" && isFavorite(m.id);
                  // Preview-mode dummy messages carry no approval field.
                  const approval = (m as { approval?: ChatApproval }).approval;
                  if (approval) {
                    return (
                      <div key={m.id} className="flex gap-2.5">
                        <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5 overflow-hidden">
                          <img src="/logos/coffee-icon-black.png" alt="Coffee" className="h-3.5 w-3.5 object-contain invert" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <AskCoffeeApprovalCard
                            approval={approval}
                            onDecide={(decision) => actions.respondToApproval(approval.card.approval_id, decision)}
                          />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
                      {m.role === "ai" && (
                        <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5 overflow-hidden">
                          <img src="/logos/coffee-icon-black.png" alt="Coffee" className="h-3.5 w-3.5 object-contain invert" />
                        </div>
                      )}
                       <div className="flex-1 min-w-0">
                         {(() => {
                           const hasReportChips = m.role === "ai" && m.text.includes(BUILD_REPORT_CHIPS);
                           const cleanText = hasReportChips ? m.text.replace(BUILD_REPORT_CHIPS, "").trimEnd() : m.text;
                           return (
                             <div className={cn(
                               "rounded-lg px-3 py-2 text-sm",
                               m.role === "user" ? "bg-accent text-accent-foreground ml-auto max-w-[85%]" : "bg-card border border-border"
                             )}>
                               {m.role === "ai" ? (
                                  <div className="prose prose-sm dark:prose-invert max-w-none" data-ai-message-content={m.id}>
                                   <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>{cleanText}</ReactMarkdown>
                                 </div>
                               ) : m.text}
                               {hasReportChips && (
                                 <div className="mt-3 grid grid-cols-1 gap-2">
                                   {(Object.keys(REPORT_TEMPLATE_META) as ReportTemplate[]).filter(isTemplateAvailable).map(k => (
                                     <button
                                       key={k}
                                       onClick={() => {
                                         onOpenChange(false);
                                          navigate(`/reports/${k}`);
                                       }}
                                       className="text-left rounded-lg border border-border bg-background p-2 hover:bg-muted transition"
                                     >
                                       <div className="flex items-start gap-2">
                                         <FileBarChart className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
                                         <div className="min-w-0">
                                           <div className="text-xs font-semibold text-foreground truncate">{REPORT_TEMPLATE_META[k].title}</div>
                                           <div className="text-[11px] text-muted-foreground line-clamp-2">{REPORT_TEMPLATE_META[k].subtitle}</div>
                                         </div>
                                       </div>
                                     </button>
                                   ))}
                                 </div>
                               )}
                             </div>
                           );
                         })()}
                        {m.role === "ai" && (
                          <div className="flex items-center gap-0.5 mt-1 ml-0.5">
                            <button onClick={() => handleFavoriteQuery(precedingUserMsg, m.text, m.id)} title={t("askCoffee.saveToFavorites")} aria-label={t("askCoffee.saveToFavorites")} className="text-[11px] text-muted-foreground hover:text-foreground transition px-1 py-0.5 rounded hover:bg-muted/50">
                              <Star className={cn("h-3 w-3", queryFavorited ? "fill-accent text-accent" : "")} />
                            </button>
                            <button onClick={() => copyRichText({ selector: `[data-ai-message-content="${m.id}"]`, plain: m.text })} title={t("askCoffee.copy")} aria-label={t("askCoffee.copy")} className="text-[11px] text-muted-foreground hover:text-foreground transition px-1 py-0.5 rounded hover:bg-muted/50">
                              <Copy className="h-3 w-3" />
                            </button>
                            <AskCoffeeFeedback messageId={m.backendMessageId} initialVote={m.vote} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Show streaming response */}
                {currentResponse && (
                  <div className="flex gap-2.5">
                    <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5 overflow-hidden">
                      <img src="/logos/coffee-icon-black.png" alt="Coffee" className="h-3.5 w-3.5 object-contain invert" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="rounded-lg px-3 py-2 text-sm bg-card border border-border">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>{currentResponse}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Show thinking indicator */}
                {isThinking && !currentResponse && (
                  <div className="flex gap-2.5">
                    <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0 mt-0.5 overflow-hidden">
                      <img src="/logos/coffee-icon-black.png" alt="Coffee" className="h-3.5 w-3.5 object-contain invert" />
                    </div>
                    <div className="rounded-lg px-3 py-2 bg-card border border-border">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground animate-pulse">
                          {t("askCoffee.brewing")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {thinkingSubtext}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-3 shrink-0 relative">
            {/* Autocomplete dropdown */}
            {input.trim().length >= 2 && (
              <div className="absolute bottom-full left-3 right-3 mb-1 rounded-lg border border-border bg-card shadow-lg overflow-hidden z-50">
                {autocompleteResults.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 border-b border-border">
                      <p className="text-[11px] font-medium text-muted-foreground">{t("askCoffee.records")}</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {autocompleteResults.map(r => (
                        <button
                          key={`${r.type}-${r.id}`}
                          onClick={() => handleAutocompleteSelect(r)}
                          className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-muted/50 transition"
                        >
                          <div className="h-6 w-6 rounded-md bg-muted flex items-center justify-center shrink-0">
                            {r.type === "person" && <User className="h-3.5 w-3.5 text-muted-foreground" />}
                            {r.type === "company" && <Building2 className="h-3.5 w-3.5 text-muted-foreground" />}
                            {r.type === "deal" && <Handshake className="h-3.5 w-3.5 text-muted-foreground" />}
                            {r.type === "meeting" && <Video className="h-3.5 w-3.5 text-muted-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{r.subtitle}</p>
                          </div>
                          <span className="text-[10px] text-muted-foreground/60 capitalize shrink-0">{r.type}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {autocompleteResults.length === 0 && (
                  <button
                    onClick={() => sendMessage(input.trim())}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-muted/50 transition"
                  >
                    <div className="h-6 w-6 rounded-md bg-warning/10 flex items-center justify-center shrink-0">
                      <Sparkles className="h-3.5 w-3.5 text-warning" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t("askCoffee.askCoffeeQuery", { query: input.trim() })}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{t("askCoffee.searchWithAi")}</p>
                    </div>
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 rounded-lg border border-input bg-card px-3 py-2 focus-within:ring-2 focus-within:ring-ring/20 transition">
              <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
                <PopoverContent
                  side="top"
                  align="end"
                  className="w-[min(calc(50vw-3rem),720px)] min-w-80 p-0 rounded-lg border border-border bg-popover shadow-xl"
                  sideOffset={8}
                >
                  <div className="px-3 pt-3 pb-2 border-b border-border">
                    <div className="flex items-center gap-2 rounded-md border border-input bg-muted/30 px-2.5 py-1.5">
                      <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <input
                        type="text"
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        placeholder={t("askCoffee.searchHistory")}
                        className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div
                    className="max-h-72 overflow-y-auto overscroll-contain scrollbar-thin"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                  >
                    {historyLoading ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : allConversations.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-6">{t("askCoffee.noHistory")}</p>
                    ) : (
                      <div className="py-1">
                        {allConversations.map((c: any) => {
                          const isSearch = c.historyType === "search";
                          const searchIcon = c.searchType === "person" ? <User className="h-3 w-3" />
                            : c.searchType === "company" ? <Building2 className="h-3 w-3" />
                            : c.searchType === "deal" ? <Handshake className="h-3 w-3" />
                            : null;
                          return (
                            <button
                              key={c.id}
                              onClick={() => handleHistoryItemClick(c)}
                              className="w-full text-left px-3 py-2 hover:bg-muted/50 transition group flex items-center gap-2.5"
                            >
                              <div className="h-5 w-5 rounded-md bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
                                {isSearch && searchIcon ? searchIcon : <Sparkles className="h-3 w-3" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-foreground break-words">
                                  {c.firstMessage || t("askCoffee.untitledChat")}
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                  {isSearch
                                    ? t("askCoffee.historySearched", {
                                        type: SEARCH_TYPE_LABEL_KEYS[c.searchType]
                                          ? t(SEARCH_TYPE_LABEL_KEYS[c.searchType])
                                          : c.searchType,
                                      })
                                    : t("askCoffee.historyPrompt")} · {c.relativeTime}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              <input
                ref={inputRef as any}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendMessage(input); } }}
                placeholder={t("askCoffee.searchOrPrompt")}
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
                autoFocus
              />
              <PopoverTrigger asChild>
                <button
                  onClick={handleOpenHistory}
                  className="flex items-center gap-1.5 shrink-0 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition"
                  aria-label={t("askCoffee.openHistory")}
                >
                  <History className="h-3.5 w-3.5" />
                  <span>{t("askCoffee.history")}</span>
                </button>
              </PopoverTrigger>
              <button onClick={() => sendMessage(input)} className="rounded-md bg-accent text-accent-foreground px-2.5 py-2 hover:opacity-90 transition shrink-0">
                <Send className="h-3.5 w-3.5" />
              </button>
              </Popover>
            </div>
          </div >
        </SheetContent >
      </Sheet >
      {/* Person/Company/Deal detail sheets are rendered globally by AppLayout via ?personSheet/?companySheet/?dealSheet */}
      <Suspense fallback={null}>
        {briefingMeeting && <MeetingBriefingSheet open={!!briefingMeeting} onOpenChange={(v) => { if (!v) setBriefingMeeting(null); }} meeting={briefingMeeting} hideOverlay={true} onBack={() => setBriefingMeeting(null)} />}
        {intelligenceNote && <MeetingIntelligenceSheet open={!!intelligenceNote} onOpenChange={(v) => { if (!v) setIntelligenceNote(null); }} note={intelligenceNote} hideOverlay={true} onBack={() => setIntelligenceNote(null)} />}
      </Suspense>
    </>
  );
}
